import React, { useState, useEffect, ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { healthService, setCurrentUser } from '../../services/health';
import { UserDto } from '../../services/users/types';
import { getUsers } from '../../services/users/usersService';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import FormSelect from '../../components/Forms/FormSelect';
import { HealthProgramGroupDto } from '../../services/health/types';

const ProgramGroupUserAttach: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [attaching, setAttaching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [programGroup, setProgramGroup] = useState<HealthProgramGroupDto | null>(null);

  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (auth.user && id) {
          // Fetch both program group and users in parallel
          const [programGroupData, usersResponse] = await Promise.all([
            healthService.getProgramGroupById(id),
            getUsers(auth.user, 1, 100)
          ]);
          
          setProgramGroup(programGroupData);
          setUsers(usersResponse.content || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (auth.user && id) {
      fetchData();
    }
  }, [auth.user, id]);

  const handleAttachUser = async () => {
    if (!id || !selectedUserId) return;

    setAttaching(true);
    setError(null);
    
    try {
      await healthService.attachProgramGroupUser(id, selectedUserId);
      navigate(`/health/program-groups/${id}?tab=users`);
    } catch (err) {
      console.error('Error attaching user:', err);
      setError('Failed to attach user. Please try again.');
    } finally {
      setAttaching(false);
    }
  };

  const handleCancel = () => {
    navigate(`/health/program-groups/${id}?tab=users`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Attach User to Program Group"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups", to: "/health/program-groups" },
          { label: programGroup?.name || 'Program Group', to: `/health/program-groups/${id}` },
          { label: "Attach User" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/health/program-groups/${id}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Program Group
          </Button>
        }
      />

      <Card className="mt-6">
        <div className="p-4 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md">
              {error}
            </div>
          )}
          
          <FormSelect
            label="Select User"
            value={selectedUserId}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedUserId(e.target.value)}
            required
            options={[
              { value: "", label: "Select a user..." },
              ...users.map(user => ({
                value: user.id || '',
                label: user.first_name && user.last_name 
                  ? `${user.first_name} ${user.last_name} (${user.email || user.username || 'No email'})`
                  : user.email || user.username || 'Unknown User'
              }))
            ]}
          />

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={attaching}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleAttachUser}
              disabled={attaching || !selectedUserId}
            >
              {attaching ? 'Attaching...' : 'Attach User'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProgramGroupUserAttach;